import { capitalizeEveryWord } from "@/utils/capitalizeEveryWord";
import { Anchor, Flex, Breadcrumbs as MantineBreadcrumbs, Skeleton } from "@mantine/core";
import { Tooltip } from "@onengine/component-library";
import { IconDots, IconLink } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import React, { type ReactElement } from "react";

const isLoadingItem = (title: string) => title?.includes("Loading") || title === "Loading...";

type BreadcrumbItem = {
  title: string;
  realHref: string;
  maskedHref: string;
  icon?: React.ReactNode;
};

type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  showLinkButton?: boolean;
  linkButtonClickHandler?: () => void;
  maxItems?: number;
};

export function Breadcrumbs({
  items,
  showLinkButton = true,
  linkButtonClickHandler,
  maxItems = 5,
}: BreadcrumbsProps) {
  let itemsComponents: React.ReactNode[] = [];

  // Get icon from first breadcrumb item
  const firstItemIcon = items[0]?.icon;

  // Clone icon with consistent styling for proper alignment and color
  const displayIcon =
    firstItemIcon && React.isValidElement(firstItemIcon)
      ? React.cloneElement(
          firstItemIcon as ReactElement<{
            size?: number;
            style?: React.CSSProperties;
            color?: string;
          }>,
          {
            size: 18,
            style: { flexShrink: 0 },
            color: "var(--text-brand-default)",
          },
        )
      : firstItemIcon;

  const renderAnchor = (item: BreadcrumbItem, isLastItem: boolean, key: React.Key) => {
    // Show skeleton for loading items
    if (isLoadingItem(item.title)) {
      return <Skeleton key={key} height={16} width={80} />;
    }

    return (
      <Anchor
        key={key}
        component={Link as any}
        // internal navigation (ids)
        to={item.realHref as any}
        // masked URL (numbers) correct behind the scenes
        mask={{ to: item.maskedHref as any }}
        c={isLastItem ? "var(--text-default)" : "var(--text-brand-default)"}
      >
        {capitalizeEveryWord(item.title)}
      </Anchor>
    );
  };

  if (items.length <= 1) {
    const only = items[0];
    // Single item (landing page) should also be in primary/brand color
    itemsComponents = [renderAnchor(only, false, 0)];
  } else if (items.length > maxItems) {
    const lastTwoItems = items.slice(-2);
    const hiddenItems = items.slice(1, -2);

    itemsComponents = [
      renderAnchor(items[0], false, 0),

      <Tooltip
        key="dots"
        content={hiddenItems.map((i) => i.title).join(" / ")}
        pointer="top-center"
      >
        <IconDots size={20} />
      </Tooltip>,

      ...lastTwoItems.map((item, index) => {
        const isLastItem = index === lastTwoItems.length - 1;
        return renderAnchor(item, isLastItem, `last-${index}`);
      }),
    ];
  } else {
    itemsComponents = items.map((item, index) => {
      const isLastItem = index === items.length - 1;
      return renderAnchor(item, isLastItem, index);
    });
  }

  return (
    <Flex gap="4" align="center">
      {displayIcon}
      <MantineBreadcrumbs>{itemsComponents}</MantineBreadcrumbs>
      {showLinkButton && (
        <IconLink
          style={{ color: "inherit", cursor: "pointer" }}
          size={16}
          onClick={() => {
            linkButtonClickHandler?.();
          }}
        />
      )}
    </Flex>
  );
}
